import { avatarDefault } from '@/assets/images';
import { Divider, Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { TrackingStateView } from '@/features/player-session/components/overview-session/tracking-state-view';
import { PlayerCardAvatar } from '@/features/player/components/list-player/player-card/player-card-avatar';
import { PlayerCardDesc } from '@/features/player/components/list-player/player-card/player-card-desc';
import {
  PlayerCardIndicator,
  PlayerCardRoot,
} from '@/features/player/components/list-player/player-card/player-card-root';
import { PlayerCardTitle } from '@/features/player/components/list-player/player-card/player-card-title';
import { getAvatarCacheBuster } from '@/features/player/utils/avatar-cache-buster';
import { createStyles, useScaling } from '@/hooks';
import { colors, ColorToken, withOpacity } from '@/theme/colors';
import React, { Activity, memo } from 'react';
import { Platform, StyleSheet, TouchableOpacity } from 'react-native';
import { usePlayerSessionCard } from '../../hooks/use-player-session-card';
import {
  getSkillIconName,
  getStepPanelTitle,
  stateList,
  StepViewMode,
} from './player-session-card.utils';
import { SkillAchievedPanel } from './skill-achieved-panel';
import { StepDetailPanel } from './step-detail-panel';

export const PlayerSessionCard = memo(
  ({ player }: { player: playerSession.SessionLookupData }) => {
    const { scale, isTablet } = useScaling();
    const avatarSize = scale(40);
    const styles = useStyles();
    const vm = usePlayerSessionCard(player);
    const activeBadge = vm.activeBadge;

    const activeStepIdx = vm.activeStepIdx;
    const stepViewMode = vm.stepViewMode;
    const progress = vm.progress;
    const subtitle = vm.subtitle;
    const isCheckpointMode = vm.isCheckpointMode;
    const skillAchieved = vm.skillAchieved;
    const checkpointItems = vm.checkpointItems;
    const stepCandidates = vm.stepCandidates;
    const activeStep = vm.activeStep;
    const activeStepLabel = vm.activeStepLabel;
    const resolvedInstruction = vm.resolvedInstruction;
    const avatarSource = vm.avatarSource ?? avatarDefault;
    const achievedPinUrl = vm.achievedPinUrl;
    const levelCompletePinUrl = vm.levelCompletePinUrl;
    const displayLevel = vm.displayLevel;
    const isSkillAchievedPanelVisible =
      stepViewMode === StepViewMode.LevelComplete ||
      (stepViewMode === StepViewMode.Achieved &&
        isCheckpointMode &&
        Boolean(activeBadge) &&
        skillAchieved);
    // When Level Complete / Skill Achieved panel is up, hide Return (BACK on certificate is the exit).
    const showReturn =
      !isSkillAchievedPanelVisible && (isCheckpointMode || stepViewMode === StepViewMode.Achieved);

    return (
      <Stack paddingX={scale({ phone: 18, tablet: 0 })}>
        <Stack width={'100%'} style={styles.playerSessionCard} borderRadius={2}>
          <PlayerCardRoot
            onPress={vm.onPressProfile}
            hideShadow
            borderRadius={0}
            borderTopLeftRadius={2}
            borderTopRightRadius={2}
            overflow="hidden"
            height={scale(49)}
            marginX={0}>
            <PlayerCardIndicator color={colors[`Level ${displayLevel}` as ColorToken]} />
            <PlayerCardAvatar
              size={avatarSize}
              source={avatarSource}
              onPress={vm.onPressProfile}
              hideProfileLabel={true}
            />

            <Stack flex={1} direction="row" justify="space-between" align="center" gap={scale(8)}>
              <Stack gap={scale(2)}>
                <PlayerCardTitle
                  title={player.name.replace(STRINGS.player.listPlayers.creatingPlayerMarker, '')}
                />
                <PlayerCardDesc>{subtitle}</PlayerCardDesc>
              </Stack>
            </Stack>
            <Activity mode={showReturn ? 'visible' : 'hidden'}>
              <TouchableOpacity
                activeOpacity={0.8}
                onPress={vm.onPressReturn}
                style={styles.returnButton}>
                <Stack direction="row" align="center" gap={scale(2)}>
                  <SvgIcon name="IcBackBlue" size={scale(16)} />
                  <Text
                    fontWeight={'600'}
                    fontSize={scale(11)}
                    fontFamily="Inter"
                    style={styles.returnText}>
                    {STRINGS.common.return}
                  </Text>
                </Stack>
              </TouchableOpacity>
            </Activity>
          </PlayerCardRoot>
          <Divider thickness={StyleSheet.hairlineWidth} color={colors['10']} />
          <Stack
            backgroundColor="white"
            borderBottomLeftRadius={2}
            borderBottomRightRadius={2}
            overflow="hidden"
            style={{
              borderLeftWidth: scale(9),
              borderLeftColor: withOpacity(colors[`Level ${displayLevel}` as ColorToken], 0.2),
            }}>
            {activeStep ? (
              <StepDetailPanel
                activeBadge={vm.activeBadge}
                levelColor={colors[`Level ${displayLevel}` as ColorToken]}
                levelNumber={displayLevel}
                skillName={resolvedInstruction}
                activeStepIdx={activeStepIdx ?? 0}
                stepCount={stepCandidates.length}
                title={getStepPanelTitle(stepViewMode, activeStepLabel)}
                body={
                  stepViewMode === StepViewMode.Incomplete
                    ? STRINGS.playerSession.stepDetail.incompleteBody
                    : resolvedInstruction
                }
                mode={stepViewMode}
                isLoading={vm.isUpdatingProgress}
                onComplete={vm.onCompleteStep}
                onIncomplete={vm.onIncompleteStep}
                onTryAgain={vm.onTryAgain}
                onSkip={vm.onSkip}
              />
            ) : stepViewMode === StepViewMode.LevelComplete ? (
              <SkillAchievedPanel
                level={displayLevel}
                title={STRINGS.playerSession.stepDetail.levelCompleteTitle(
                  `Level ${displayLevel}`,
                  isTablet,
                )}
                buttonLabel={
                  displayLevel === 10
                    ? STRINGS.playerSession.stepDetail.levelCompleteBack
                    : STRINGS.playerSession.stepDetail.nextLevel
                }
                icon={'IcMastery'}
                pinUrl={levelCompletePinUrl}
                levelColor={`Level ${displayLevel}` as ColorToken}
                onPress={displayLevel === 10 ? vm.onBackFromLevelComplete : vm.onNextLevel}
              />
            ) : stepViewMode === StepViewMode.Achieved &&
              isCheckpointMode &&
              activeBadge &&
              skillAchieved ? (
              <SkillAchievedPanel
                level={displayLevel}
                title={STRINGS.playerSession.stepDetail.achievedTitle(
                  `Level ${displayLevel}`,
                  activeBadge.label,
                  isTablet,
                )}
                buttonLabel={STRINGS.playerSession.stepDetail.nextSkill}
                icon={getSkillIconName(activeBadge.name)}
                pinUrl={achievedPinUrl}
                levelColor={`Level ${displayLevel}` as ColorToken}
                onPress={vm.onNextSkill}
              />
            ) : (
              <Stack
                height={scale({ phone: 74, tablet: 88 })}
                justify="center"
                align="center"
                backgroundColor={'white'}
                overflow="hidden"
                gap={scale(8)}
                direction="row">
                <Activity mode={'visible'}>
                  {isCheckpointMode
                    ? checkpointItems.map(item => (
                        <TrackingStateView
                          key={`${item.key}-${player.id}`}
                          icon={item.icon}
                          label={item.label}
                          level={displayLevel}
                          data={item.data}
                          hideProgress={item.hideProgress}
                          onPress={() => vm.onPressCheckpointItem(item)}
                        />
                      ))
                    : stateList.map(state => (
                        <TrackingStateView
                          key={`${state.name}-${player.id}`}
                          icon={state.icon}
                          label={state.label}
                          level={displayLevel}
                          data={progress?.badges.find(badge => badge.name === state.name)}
                          onPress={() =>
                            vm.onPressStateSkill({ name: state.name, label: state.label })
                          }
                        />
                      ))}
                </Activity>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  },
  (prev, next) => {
    const prevV = getAvatarCacheBuster(prev.player.id);
    const nextV = getAvatarCacheBuster(next.player.id);
    return (
      prev.player.id === next.player.id &&
      prev.player.name === next.player.name &&
      prev.player.level === next.player.level &&
      prev.player.avatar_url === next.player.avatar_url &&
      prevV === nextV &&
      JSON.stringify(prev.player.progress ?? null) === JSON.stringify(next.player.progress ?? null)
    );
  },
);

const useStyles = createStyles(({ scale }) => ({
  playerSessionCard: {
    backgroundColor: 'white',
    borderRadius: 2,
    overflow: 'hidden',
    ...(Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 3,
      },
      android: {
        elevation: 2,
      },
    }) ?? {}),
  },
  returnButton: {
    paddingRight: scale(10),
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  returnText: {
    color: colors.Blue,
  },
}));
